import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Video } from "@/types/videos";
import { logError, logWarning } from "@/services/errorLogger";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formate la taille d'un fichier en format lisible (KB, MB, GB)
 * @param bytes - Taille en bytes
 * @returns Chaîne formatée (ex: "15.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Valide qu'une vidéo contient toutes les propriétés requises
 * @param video - L'objet vidéo à valider
 * @returns true si la vidéo est valide, false sinon
 */
export function isValidVideo(video: any): video is Video {
  return (
    video !== null &&
    video !== undefined &&
    typeof video === 'object' &&
    typeof video.id === 'string' &&
    video.id.length > 0 &&
    typeof video.video_url === 'string' &&
    video.video_url.length > 0 &&
    typeof video.user_id === 'string' &&
    video.profiles !== null &&
    video.profiles !== undefined
  );
}

/**
 * Filtre un tableau de vidéos pour ne garder que les vidéos valides
 * @param videos - Tableau de vidéos à filtrer
 * @returns Tableau contenant uniquement les vidéos valides
 */
export function filterValidVideos(videos: any[]): Video[] {
  if (!Array.isArray(videos)) {
    logError('[filterValidVideos] Input is not an array', new Error('Invalid input'), { input: typeof videos });
    return [];
  }
  
  const validVideos = videos.filter(isValidVideo);
  
  if (validVideos.length < videos.length) {
    logWarning(
      `[filterValidVideos] Filtered out ${videos.length - validVideos.length} invalid video(s)`,
      { total: videos.length, valid: validVideos.length }
    );
  }
  
  return validVideos;
}
